import { Link } from 'react-router-dom';
import {
  wallet,
  fiat,
  rates,
  onramps,
  offramps,
  ApiClientError,
  type WalletInfo,
  type FiatAccountInfo,
} from '../lib/api';
import { useResource } from '../hooks/useResource';
import { formatDateTime } from '../lib/format';
import { Badge, Card, Empty, Spinner } from '../components/ui';

/**
 * Balance-first landing page: both inventories, funding entry points, current
 * rates, and the latest ramp activity. A 404 on wallet/fiat means "not created
 * yet" — rendered as a setup prompt, not an error.
 */

function orderTone(status: string): 'success' | 'warning' | 'danger' | 'neutral' {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'expired') return 'neutral';
  return 'warning';
}

function notFoundAsNull<T>(promise: Promise<T>): Promise<T | null> {
  return promise.catch((error) => {
    if (error instanceof ApiClientError && error.status === 404) return null;
    throw error;
  });
}

export function Overview() {
  const walletInfo = useResource<WalletInfo | null>(() => notFoundAsNull(wallet.get()), [], { refreshMs: 25000 });
  const fiatInfo = useResource<FiatAccountInfo | null>(() => notFoundAsNull(fiat.get()), [], { refreshMs: 25000 });
  const currentRates = useResource(() => rates.get().catch(() => null), []);
  const recentOnramps = useResource(() => onramps.list(1).catch(() => null), []);
  const recentOfframps = useResource(() => offramps.list(1).catch(() => null), []);

  if (walletInfo.loading || fiatInfo.loading) return <Spinner />;

  const recent = [
    ...(recentOnramps.data?.items ?? []).map((o) => ({
      id: o.id,
      kind: 'onramp' as const,
      when: o.created_at,
      inAmount: o.amount_ngn ? `₦${o.amount_ngn}` : (o.expected_ngn ? `~₦${o.expected_ngn}` : '—'),
      outAmount: o.amount_usdc ? `${o.amount_usdc} USDC` : '—',
      status: o.status,
    })),
    ...(recentOfframps.data?.items ?? []).map((o) => ({
      id: o.id,
      kind: 'offramp' as const,
      when: o.created_at,
      inAmount: o.amount_usdc ? `${o.amount_usdc} USDC` : '—',
      outAmount: o.amount_ngn ? `₦${o.amount_ngn}` : '—',
      status: o.status,
    })),
  ]
    .sort((a, b) => b.when.localeCompare(a.when))
    .slice(0, 8);

  return (
    <>
      <div className="page-header">
        <h1>Overview</h1>
        <p>Your inventory on both rails, and the latest ramp activity.</p>
      </div>

      <div className="stat-grid">
        <div className="stat">
          <div className="stat-label">Crypto balance</div>
          <div className="stat-value">
            {walletInfo.data ? `${walletInfo.data.balance} USDC` : '—'}
          </div>
          <div style={{ marginTop: 10 }}>
            {walletInfo.data ? (
              <Link className="btn btn-sm" to="/wallet">
                Fund / send
              </Link>
            ) : (
              <Link className="btn btn-primary btn-sm" to="/wallet">
                Create wallet
              </Link>
            )}
          </div>
        </div>

        <div className="stat">
          <div className="stat-label">Fiat balance</div>
          <div className="stat-value">{fiatInfo.data ? `₦${fiatInfo.data.balance}` : '—'}</div>
          <div style={{ marginTop: 10 }}>
            {fiatInfo.data ? (
              <Link className="btn btn-sm" to="/fiat">
                Fund / pay out
              </Link>
            ) : (
              <Link className="btn btn-primary btn-sm" to="/fiat">
                Create NGN account
              </Link>
            )}
          </div>
        </div>

        <div className="stat">
          <div className="stat-label">Your rates (₦/USDC)</div>
          <div className="stat-value" style={{ fontSize: 17 }}>
            <span className="muted" style={{ fontSize: 12 }}>buy </span>
            {currentRates.data?.offramp_rate ?? '—'}
            <span className="muted" style={{ fontSize: 12 }}> · sell </span>
            {currentRates.data?.onramp_rate ?? '—'}
          </div>
          <div style={{ marginTop: 10 }}>
            <Link className="btn btn-sm" to="/rates">
              Set rates
            </Link>
          </div>
        </div>
      </div>

      {fiatInfo.data?.deposit_account && (
        <Card title="Fund your NGN balance">
          <div className="card-body" style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <div className="stat-label">Account number</div>
              <div className="mono" style={{ fontSize: 18, fontWeight: 600 }}>
                {fiatInfo.data.deposit_account.account_number}
              </div>
            </div>
            <div>
              <div className="stat-label">Bank</div>
              <div>{fiatInfo.data.deposit_account.bank_name}</div>
            </div>
            <div>
              <div className="stat-label">Account name</div>
              <div>{fiatInfo.data.deposit_account.account_name}</div>
            </div>
            <div className="muted" style={{ alignSelf: 'center', fontSize: 12.5 }}>
              Bank transfers to this account credit your balance automatically.
            </div>
          </div>
        </Card>
      )}

      <div style={{ height: 16 }} />

      <Card
        title="Recent ramp activity"
        actions={
          <span style={{ display: 'flex', gap: 6 }}>
            <Link className="btn btn-sm" to="/onramps">
              Onramps
            </Link>
            <Link className="btn btn-sm" to="/offramps">
              Offramps
            </Link>
          </span>
        }
      >
        {!recent.length ? (
          <Empty>
            No orders yet. Create them from your backend with{' '}
            <code>POST /api/v1/onramps</code> and <code>POST /api/v1/offramps</code> — see{' '}
            <Link to="/docs">the docs</Link>.
          </Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Type</th>
                  <th>In</th>
                  <th>Out</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((order) => (
                  <tr key={order.id}>
                    <td className="muted">{formatDateTime(order.when)}</td>
                    <td>
                      <Badge tone={order.kind === 'onramp' ? 'accent' : 'neutral'}>
                        {order.kind}
                      </Badge>
                    </td>
                    <td className="mono">{order.inAmount}</td>
                    <td className="mono">{order.outAmount}</td>
                    <td>
                      <Badge tone={orderTone(order.status)}>{order.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
